import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Icon,
  Input,
  Row,
  Switch,
  TimePicker,
} from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { FormComponentProps, ValidationRule } from "antd/lib/form";
import moment from "moment";
import { FreireContext } from "../../utils/freireContext";
import { formatarMoeda } from "../FormatingUtils";
import * as texts from "../StationEditDrawer/locales";
import {
  buildCostPerHourIntermediaryFieldPath,
  COST_PER_HOUR_PEAK_RANGE_KEY,
  getCostPerHourIntermediaryRangeKey,
  detectCostPerHourOverlapKeys,
} from "./utils";

type StationCostPerHourProps = {
  loading: boolean;
  form: FormComponentProps["form"];
  selectedCompanyCurrency: string;
  resetKey: number;
  initialIntermediaryValues?: Array<{
    value?: number | null;
    start_time?: string | null;
    end_time?: string | null;
  }>;
};

const DEFAULT_WEEK_DAYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
];

type TRangeRecord = {
  key: string;
  start: moment.Moment;
  end: moment.Moment;
};

const normalizeTimeValue = (value: any) => {
  if (!value) {
    return null;
  }

  if (moment.isMoment(value)) {
    return value.clone();
  }

  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
};

type WeekdaySelectorProps = {
  value?: string[];
  onChange?: (value: string[]) => void;
  options: { label: string; value: string }[];
  disabled?: boolean;
};

const WeekdaySelector = ({
  value,
  onChange,
  options,
  disabled,
}: WeekdaySelectorProps) => {
  const selectedDays = Array.isArray(value) ? value : [];

  const handleCheckboxChange = (dayValue: string) => (
    event: CheckboxChangeEvent
  ) => {
    const nextDays = new Set(selectedDays);
    if (event.target.checked) {
      nextDays.add(dayValue);
    } else {
      nextDays.delete(dayValue);
    }
    onChange?.(Array.from(nextDays));
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
      {options.map((option) => (
        <Checkbox
          key={option.value}
          checked={selectedDays.includes(option.value)}
          onChange={handleCheckboxChange(option.value)}
          disabled={disabled}
          style={{ marginRight: 8, marginBottom: 4 }}
        >
          {option.label}
        </Checkbox>
      ))}
    </div>
  );
};

export const StationCostPerHour = ({
  loading,
  form,
  selectedCompanyCurrency,
  initialIntermediaryValues = [],
  resetKey,
}: StationCostPerHourProps) => {
  const { freire } = useContext(FreireContext);
  const { getFieldDecorator, setFieldsValue, getFieldValue } = form;
  const [intermediaryIndexes, setIntermediaryIndexes] = useState<number[]>([]);
  const intermediaryCounterRef = useRef(0);
  const [timeChangeCounter, setTimeChangeCounter] = useState(0);
  const [overlappingRangeKeys, setOverlappingRangeKeys] = useState<Set<string>>(
    new Set()
  );
  const previousCostPerHourEnabledValue = useRef<boolean | null>(null);
  const hasAutoCompletedDaysRef = useRef(false);

  const zeroCurrencyValue = useMemo(() => formatarMoeda("0"), []);
  const intermediaryValuePlaceholder = useMemo(
    () =>
      selectedCompanyCurrency
        ? `${selectedCompanyCurrency}${zeroCurrencyValue}`
        : zeroCurrencyValue,
    [selectedCompanyCurrency, zeroCurrencyValue]
  );

  const isEnergyCostConfigured =
    getFieldValue('configure_energy_cost') ?? false;
  const rawCostPerHourToggleValue =
    getFieldValue('cost_per_hour_enabled') ?? false;
  const isCostPerHourEnabled =
    isEnergyCostConfigured && rawCostPerHourToggleValue;

  useEffect(() => {
    hasAutoCompletedDaysRef.current = false;
    const limitedEntries = initialIntermediaryValues.slice(0, 2);
    const indexes = limitedEntries.map((_, idx) => idx);
    setIntermediaryIndexes(indexes);
    intermediaryCounterRef.current = indexes.length;

    if (limitedEntries.length) {
      const payload: Record<string, any> = {};
      limitedEntries.forEach((entry, index) => {
        const valuePath = buildCostPerHourIntermediaryFieldPath(index, 'value');
        const startPath = buildCostPerHourIntermediaryFieldPath(
          index,
          'start_time'
        );
        const endPath = buildCostPerHourIntermediaryFieldPath(index, 'end_time');

        payload[valuePath] = entry?.value;
        payload[startPath] = entry?.start_time;
        payload[endPath] = entry?.end_time;
      });

      setFieldsValue(payload);
    }
  }, [initialIntermediaryValues, resetKey, setFieldsValue]);

  const clearIntermediaryFields = (indexesToClear: number[]) => {
    if (!indexesToClear.length) return;

    const fieldsToReset: Record<string, undefined> = {};
    indexesToClear.forEach((index) => {
      fieldsToReset[buildCostPerHourIntermediaryFieldPath(index, 'value')] =
        undefined;
      fieldsToReset[buildCostPerHourIntermediaryFieldPath(index, 'start_time')] =
        undefined;
      fieldsToReset[buildCostPerHourIntermediaryFieldPath(index, 'end_time')] =
        undefined;
    });

    setFieldsValue(fieldsToReset);
  };

  const dayOptions = useMemo(
    () => [
      { label: freire(texts.COST_PER_HOUR_WEEKDAY_MONDAY), value: "MONDAY" },
      { label: freire(texts.COST_PER_HOUR_WEEKDAY_TUESDAY), value: "TUESDAY" },
      { label: freire(texts.COST_PER_HOUR_WEEKDAY_WEDNESDAY), value: "WEDNESDAY" },
      { label: freire(texts.COST_PER_HOUR_WEEKDAY_THURSDAY), value: "THURSDAY" },
      { label: freire(texts.COST_PER_HOUR_WEEKDAY_FRIDAY), value: "FRIDAY" },
      { label: freire(texts.COST_PER_HOUR_WEEKDAY_SATURDAY), value: "SATURDAY" },
      { label: freire(texts.COST_PER_HOUR_WEEKDAY_SUNDAY), value: "SUNDAY" },
    ],
    [freire]
  );

  const ensureDayArray = (value?: any) =>
    Array.isArray(value) ? [...value] : [];

  const clearConfiguration = () => {
    const currentTimezone =
      form.getFieldValue('cost_per_hour_configuration.timezone') ||
      moment.tz.guess();
    clearIntermediaryFields(intermediaryIndexes);
    setFieldsValue({
      'cost_per_hour_configuration.timezone': currentTimezone,
      'cost_per_hour_configuration.day_of_week': [],
      'cost_per_hour_configuration.peak_value.value': undefined,
      'cost_per_hour_configuration.peak_value.start_time': undefined,
      'cost_per_hour_configuration.peak_value.end_time': undefined,
    });
    setIntermediaryIndexes([]);
    intermediaryCounterRef.current = 0;
    setOverlappingRangeKeys(new Set());
    hasAutoCompletedDaysRef.current = false;
  };

  const handleConfigureEnergyToggle = (value: boolean) => {
    if (!value) {
      clearConfiguration();
      setFieldsValue({
        configure_energy_cost: value,
        cost_per_hour_enabled: false,
      });
      return;
    }

    setFieldsValue({ configure_energy_cost: value });
  };

  const handleToggle = (value: boolean) => {
    if (!value) {
      clearConfiguration();
    }

    setFieldsValue({ cost_per_hour_enabled: value });
  };

  useEffect(() => {
    if (!isEnergyCostConfigured) {
      previousCostPerHourEnabledValue.current = null;
      return;
    }

    // React re-renders after every validation, so bail out unless the toggle value changed.
    if (previousCostPerHourEnabledValue.current === isCostPerHourEnabled) {
      return;
    }

    if (previousCostPerHourEnabledValue.current === null) {
      previousCostPerHourEnabledValue.current = isCostPerHourEnabled;
      return;
    }

    previousCostPerHourEnabledValue.current = isCostPerHourEnabled;
    form.validateFields(['energy_cost'], { force: true });
  }, [form, isCostPerHourEnabled, isEnergyCostConfigured]);

  useEffect(() => {
    if (!isCostPerHourEnabled) {
      return;
    }

    const currentDays = form.getFieldValue(
      'cost_per_hour_configuration.day_of_week'
    );
    if (
      (!currentDays || currentDays.length === 0) &&
      !hasAutoCompletedDaysRef.current
    ) {
      hasAutoCompletedDaysRef.current = true;
      setFieldsValue({
        'cost_per_hour_configuration.day_of_week': [...DEFAULT_WEEK_DAYS],
      });
    }
  }, [form, isCostPerHourEnabled, resetKey, setFieldsValue]);

  useEffect(() => {
    if (!isCostPerHourEnabled) {
      setOverlappingRangeKeys(new Set());
      return;
    }

    const ranges: TRangeRecord[] = [];
    const peakStartValue = normalizeTimeValue(
      form.getFieldValue('cost_per_hour_configuration.peak_value.start_time')
    );
    const peakEndValue = normalizeTimeValue(
      form.getFieldValue('cost_per_hour_configuration.peak_value.end_time')
    );

    if (peakStartValue && peakEndValue) {
      ranges.push({
        key: COST_PER_HOUR_PEAK_RANGE_KEY,
        start: peakStartValue,
        end: peakEndValue,
      });
    }

    intermediaryIndexes.forEach((index) => {
      const startValue = normalizeTimeValue(
        form.getFieldValue(
          buildCostPerHourIntermediaryFieldPath(index, 'start_time')
        )
      );
      const endValue = normalizeTimeValue(
        form.getFieldValue(
          buildCostPerHourIntermediaryFieldPath(index, 'end_time')
        )
      );

      if (startValue && endValue) {
        ranges.push({
          key: getCostPerHourIntermediaryRangeKey(index),
          start: startValue,
          end: endValue,
        });
      }
    });

    setOverlappingRangeKeys(detectCostPerHourOverlapKeys(ranges));
  }, [form, intermediaryIndexes, isCostPerHourEnabled, resetKey, timeChangeCounter]);

  const handleTimeChange = () => {
    setTimeChangeCounter((prev) => prev + 1);
  };

  const buildStartTimeValidator = (endFieldPath: string) => (
    _rule: ValidationRule,
    value: moment.Moment,
    callback: any
  ) => {
    const endValue = form.getFieldValue(endFieldPath);

    if (!value || !endValue) {
      callback();
      return;
    }

    if (!value.isBefore(endValue)) {
      callback(freire(texts.COST_PER_HOUR_TIME_ORDER_HINT));
      return;
    }

    callback();
  };

  const buildEndTimeValidator = (startFieldPath: string) => (
    _rule: ValidationRule,
    value: moment.Moment,
    callback: any
  ) => {
    const startValue = form.getFieldValue(startFieldPath);

    if (!value || !startValue) {
      callback();
      return;
    }

    if (!value.isAfter(startValue)) {
      callback(freire(texts.COST_PER_HOUR_TIME_ORDER_HINT));
      return;
    }

    callback();
  };

  const overlapHintStyle = {
    color: '#f5222d',
    fontSize: 12,
    marginTop: 4,
  };

  const renderOverlapHint = (rangeKey: string) => {
    if (!overlappingRangeKeys.has(rangeKey)) {
      return null;
    }

    return (
      <div className="ant-form-explain" style={overlapHintStyle}>
        <small>{freire(texts.WARNING_COST_PER_HOUR_OVERLAP)}</small>
      </div>
    );
  };

  const handleAddIntermediary = () => {
    const nextIndex = intermediaryCounterRef.current;
    intermediaryCounterRef.current += 1;
    setIntermediaryIndexes((prev) => [...prev, nextIndex]);
  };

  const handleRemoveIntermediary = (index: number) => {
    clearIntermediaryFields([index]);
    setIntermediaryIndexes((prev) => prev.filter((item) => item !== index));
  };

  return (
    <div>
      <Row>
        <Col span={24}>
          <Form.Item style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              {getFieldDecorator('configure_energy_cost', {
                valuePropName: 'checked',
                initialValue: false,
              })(
                <Switch
                  disabled={loading}
                  checkedChildren={freire(texts.YES)}
                  unCheckedChildren={freire(texts.NO)}
                  onChange={handleConfigureEnergyToggle}
                  style={{ marginRight: 10, marginTop: 4 }}
                />
              )}
              <span style={{ display: 'inline-block' }}>
                <div>{freire(texts.CONFIGURE_ENERGY_COST_TOGGLE_LABEL)}</div>
              </span>
            </div>
          </Form.Item>
        </Col>
      </Row>

      <div style={!isEnergyCostConfigured ? { display: 'none' } : {}}>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              label={`${freire(texts.COST_PER_KWH_LABEL)} (${selectedCompanyCurrency})`}
            >
              {getFieldDecorator("energy_cost", {
                normalize: (input) => (input ? formatarMoeda(String(input)) : "0,00"),
                rules: !isCostPerHourEnabled
                  ? []
                  : [
                      {
                        required: true,
                        message: freire(
                          texts.WARNING_COST_PER_HOUR_STANDARD_REQUIRED
                        ),
                      },
                    ],
              })(
                <Input
                  placeholder={freire(texts.COST_PER_KWH_PLACEHOLDER)}
                  disabled={loading}
                  style={{ width: 160 }}
                />
              )}
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                {getFieldDecorator("cost_per_hour_enabled", {
                  valuePropName: "checked",
                  initialValue: false,
                })(
                  <Switch
                    disabled={loading}
                    checkedChildren={freire(texts.YES)}
                    unCheckedChildren={freire(texts.NO)}
                    onChange={handleToggle}
                    style={{ marginRight: 10, marginTop: 4 }}
                  />
                )}
                <span style={{ display: 'inline-block' }}>
                  <div>{freire(texts.COST_PER_HOUR_SWITCH_LABEL)}</div>
                  <small>{freire(texts.COST_PER_HOUR_SWITCH_HELP)}</small>
                </span>
              </div>
            </Form.Item>
          </Col>
        </Row>

        <div style={!isCostPerHourEnabled ? { display: "none" } : {}}>
        <Row gutter={24}>
        <Col span={8}>
          <Form.Item
            style={{ marginBottom: 12 }}
            label={freire(
              texts.COST_PER_HOUR_PEAK_VALUE(selectedCompanyCurrency)
            )}
          >
            {getFieldDecorator("cost_per_hour_configuration.peak_value.value", {
              normalize: (input: string) =>
                input ? formatarMoeda(String(input)) : "0,00",
              rules: !isCostPerHourEnabled
                ? []
                : [
                    {
                      required: true,
                      message: freire(texts.WARNING_COST_PER_HOUR_PEAK_REQUIRED),
                    },
                  ],
            })(<Input disabled={loading} style={{ width: 160 }} />)}
            {renderOverlapHint(COST_PER_HOUR_PEAK_RANGE_KEY)}
          </Form.Item>
        </Col>
        <Col span={7}>
          <Form.Item label={freire(texts.OF)} style={{ marginBottom: 12 }}>
            {getFieldDecorator("cost_per_hour_configuration.peak_value.start_time", {
              rules: !isCostPerHourEnabled
                ? []
                : [
                    {
                      required: true,
                      message: freire(texts.WARNING_COST_PER_HOUR_PEAK_REQUIRED),
                    },
                    {
                      validator: buildStartTimeValidator(
                        'cost_per_hour_configuration.peak_value.end_time'
                      ),
                    },
                  ],
            })(
              <TimePicker
                style={{ width: "100%" }}
                format="HH:mm"
                disabled={loading}
                onChange={handleTimeChange}
              />
            )}
          </Form.Item>
        </Col>
        <Col span={7}>
          <Form.Item label={freire(texts.TO)} style={{ marginBottom: 12 }}>
            {getFieldDecorator("cost_per_hour_configuration.peak_value.end_time", {
              rules: !isCostPerHourEnabled
                ? []
                : [
                    {
                      required: true,
                      message: freire(texts.WARNING_COST_PER_HOUR_PEAK_REQUIRED),
                    },
                    {
                      validator: buildEndTimeValidator(
                        'cost_per_hour_configuration.peak_value.start_time'
                      ),
                    },
                  ],
            })(
              <TimePicker
                style={{ width: "100%" }}
                format="HH:mm"
                disabled={loading}
                onChange={handleTimeChange}
              />
            )}
          </Form.Item>
        </Col>
      </Row>

      {intermediaryIndexes.map((index, position) => {
        const valueField = buildCostPerHourIntermediaryFieldPath(index, 'value');
        const startField = buildCostPerHourIntermediaryFieldPath(
          index,
          'start_time'
        );
        const endField = buildCostPerHourIntermediaryFieldPath(
          index,
          'end_time'
        );
        const rangeKey = getCostPerHourIntermediaryRangeKey(index);

        return (
          <React.Fragment key={index}>
            <Row
              gutter={24}
              type="flex"
              align="middle"
              style={{ marginTop: position > 0 ? -12 : 0 }}
            >
              <Col span={8}>
                <Form.Item
                  label={freire(
                    texts.COST_PER_HOUR_INTERMEDIARY_SIMPLE_VALUE(
                      selectedCompanyCurrency
                    )
                  )}
                >
                  {getFieldDecorator(valueField, {
                    normalize: (input: string) =>
                      input ? formatarMoeda(String(input)) : "0,00",
                    rules: !isCostPerHourEnabled
                      ? []
                      : [
                          {
                            required: true,
                            message: freire(
                              texts.WARNING_COST_PER_HOUR_INCOMPLETE_INTERMEDIARY
                            ),
                          },
                        ],
                  })(
                    <Input
                      placeholder={intermediaryValuePlaceholder}
                      disabled={loading}
                      style={{ width: 160 }}
                    />
                  )}
                  {renderOverlapHint(rangeKey)}
                </Form.Item>
              </Col>
              <Col span={7}>
                <Form.Item label={freire(texts.OF)}>
                  {getFieldDecorator(startField, {
                    rules: !isCostPerHourEnabled
                      ? []
                      : [
                          {
                            required: true,
                            message: freire(
                              texts.WARNING_COST_PER_HOUR_INCOMPLETE_INTERMEDIARY
                            ),
                          },
                          {
                            validator: buildStartTimeValidator(
                              buildCostPerHourIntermediaryFieldPath(
                                index,
                                'end_time'
                              )
                            ),
                          },
                        ],
                  })(
                    <TimePicker
                      style={{ width: "100%" }}
                      format="HH:mm"
                      disabled={loading}
                      onChange={handleTimeChange}
                    />
                  )}
                </Form.Item>
              </Col>
              <Col span={7}>
                <Form.Item label={freire(texts.TO)}>
                  {getFieldDecorator(endField, {
                    rules: !isCostPerHourEnabled
                      ? []
                      : [
                          {
                            required: true,
                            message: freire(
                              texts.WARNING_COST_PER_HOUR_INCOMPLETE_INTERMEDIARY
                            ),
                          },
                          {
                            validator: buildEndTimeValidator(
                              buildCostPerHourIntermediaryFieldPath(
                                index,
                                'start_time'
                              )
                            ),
                          },
                        ],
                  })(
                    <TimePicker
                      style={{ width: "100%" }}
                      format="HH:mm"
                      disabled={loading}
                      onChange={handleTimeChange}
                    />
                  )}
                </Form.Item>
              </Col>
              <Col>
                <Icon
                  type="delete"
                  onClick={() => handleRemoveIntermediary(index)}
                  style={{ cursor: 'pointer' }}
                />
              </Col>
            </Row>
          </React.Fragment>
        );
      })}

        {intermediaryIndexes.length < 2 && isCostPerHourEnabled && (
          <Row>
            <Col span={24}>
              <Button
                type="dashed"
                icon="plus"
                block
                onClick={handleAddIntermediary}
                disabled={loading}
                style={{ marginBottom: 16 }}
              >
                {freire(texts.COST_PER_HOUR_ADD_INTERMEDIARY)}
              </Button>
            </Col>
          </Row>
        )}

        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              label={freire(texts.COST_PER_HOUR_DAYS_LABEL)}
            >
              <div style={{ marginBottom: 8, color: 'rgba(0,0,0,0.65)' }}>
                <small>{freire(texts.COST_PER_HOUR_DAYS_HELP)}</small>
              </div>
              {getFieldDecorator("cost_per_hour_configuration.day_of_week", {
                validateTrigger: ['onChange', 'onBlur'],
                normalize: ensureDayArray,
                rules: !isCostPerHourEnabled
                  ? []
                  : [
                      {
                        required: true,
                        message: freire(texts.WARNING_COST_PER_HOUR_DAYS_REQUIRED),
                      },
                    ],
              })(
                <WeekdaySelector options={dayOptions} disabled={loading} />
              )}
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ display: "none" }}>
          {getFieldDecorator("cost_per_hour_configuration.timezone", {
            initialValue: moment.tz.guess(),
          })(<Input type="hidden" />)}
        </Form.Item>
      </div>
    </div>
  </div>
  );
};
